import { toLocalISODate, toLocalMonth, parseLocalDate, toDateInputValue, addMonths, monthBounds, monthOf } from '../utils/dateUtils';
import { confirmDialog, reportLoadError, notify } from '../utils/notify';
import { filtersIncluding, monthFiltersIncluding } from '../utils/financeFilters';
import React, { useState, useEffect, useContext } from 'react';
import { financeAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/Modal';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ErrorBoundary from '../components/ErrorBoundary';
import {
  Banknote, ChartColumn, ChartLine, ChartPie, Check, ChevronLeft, ChevronRight, Coins, Flame, History, Hourglass, List,
  Paperclip, Pencil, Plus, Receipt, Tag, Target, Trash2, TriangleAlert, X,
} from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { chartColors, CHART_FONT } from '../utils/chartTheme';
import './Finance.css';

const Finance = () => {
  const { user } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  const [activeTab, setActiveTab] = useState('expenses');
  const [expenses, setExpenses] = useState([]);
  const [revenues, setRevenues] = useState([]); 
  const [budgets, setBudgets] = useState([]);
  const [budgetComparison, setBudgetComparison] = useState([]);
  const [trends, setTrends] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [showCreateExpenseModal, setShowCreateExpenseModal] = useState(false);
  const [showEditExpenseModal, setShowEditExpenseModal] = useState(false);
  const [showCreateBudgetModal, setShowCreateBudgetModal] = useState(false);
  const [showEditBudgetModal, setShowEditBudgetModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showCreateRevenueModal, setShowCreateRevenueModal] = useState(false);
  const [showEditRevenueModal, setShowEditRevenueModal] = useState(false);
  const [editingRevenue, setEditingRevenue] = useState(null);
  
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingBudget, setEditingBudget] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [burnRate, setBurnRate] = useState(null);
  const [runway, setRunway] = useState(null);
  // Computed by the backend: opening balance + approved revenues - approved
  // expenses, for the filter's currency. It used to be a number typed here.
  const [cashOnHand, setCashOnHand] = useState(null);
  const [showOpeningBalanceModal, setShowOpeningBalanceModal] = useState(false);
  const [openingBalanceInput, setOpeningBalanceInput] = useState('');
  
  const [expenseForm, setExpenseForm] = useState({
    category: 'Office Supplies',
    subcategory: '',
    amount: '',
    currency: 'USD',
    description: '',
    expense_date: toLocalISODate(new Date()),
    tax_deductible: false,
    category_type: 'operational',
    budget_id: null,
    customCategory: ''
  });

  // Expense category hierarchy
  const EXPENSE_CATEGORIES = {
    'Office Supplies': ['Stationery', 'Paper & Printing', 'Office Equipment', 'Furniture', 'Other'],
    'Equipment': ['Computers', 'Software Licenses', 'Tools', 'Machinery', 'Other'],
    'Travel': ['Airfare', 'Hotel', 'Meals', 'Local Transport', 'Car Rental', 'Other'],
    'Utilities': ['Electricity', 'Water', 'Internet', 'Phone', 'Gas', 'Other'],
    'Salaries': ['Base Salary', 'Bonuses', 'Overtime', 'Benefits', 'Payroll Consolidation', 'Other'],
    'Marketing': ['Digital Ads', 'Print Media', 'Social Media', 'Events', 'Sponsorships', 'Other'],
    'Other': ['Miscellaneous']
  };

  const REVENUE_CATEGORIES = {
    'Sales': 'Product/Service Sales',
    'Investment': 'Investment Income',
    'Consulting': 'Consulting Fees',
    'Grants': 'Grants & Funding',
    'Interest': 'Interest Income',
    'Other': 'Other Revenue'
  };

  const [availableBudgets, setAvailableBudgets] = useState([]);

  useEffect(() => {
    if (showCreateExpenseModal && expenseForm.category && expenseForm.currency) {
      financeAPI.getBudgets({ category: expenseForm.category, currency: expenseForm.currency })
        .then(res => setAvailableBudgets(res.data.budgets))
        .catch(err => console.error('Error loading budgets:', err));
    }
  }, [showCreateExpenseModal, expenseForm.category, expenseForm.currency]);

  const [revenueForm, setRevenueForm] = useState({
    category: 'Sales',
    amount: '',
    currency: 'USD',
    description: '',
    revenue_date: toLocalISODate(new Date()),
    payment_method: '',
    customCategory: ''
  });

  const [budgetForm, setBudgetForm] = useState({
    category: 'Office Supplies',
    currency: 'USD',
    amount: '',
    period_type: 'monthly',
    start_date: toLocalISODate(new Date()),
    end_date: '',
    customCategory: ''
  });

  const [filters, setFilters] = useState({
    start_date: monthBounds(toLocalMonth()).start,
    end_date: monthBounds(toLocalMonth()).end,
    category: '',
    status: '',
    currency: 'USD'
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTab]);

  // Expenses and revenues have different categories: a category chosen on
  // one tab, now that revenues honour it, would empty the other tab's list.
  const switchTab = (tab) => {
    setActiveTab(tab);
    setFilters(f => (f.category ? { ...f, category: '' } : f));
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'expenses') {
        const [expensesRes, summaryRes, burnRateRes, cashRes] = await Promise.all([
          financeAPI.getExpenses(filters),
          financeAPI.getSummary(filters),
          financeAPI.getBurnRate({ currency: filters.currency, months: 3 }),
          financeAPI.getCashOnHand({ currency: filters.currency })
        ]);
        setExpenses(expensesRes.data.expenses);
        setSummary(summaryRes.data.summary);
        setCashOnHand(cashRes.data);
        setBurnRate(burnRateRes.data);
        
        if (parseFloat(burnRateRes.data.burn_rate) > 0) {
          const runwayRes = await financeAPI.getRunway({ currency: filters.currency });
          setRunway(runwayRes.data);
        }
      } else if (activeTab === 'revenues') {
        const [revenuesRes, summaryRes] = await Promise.all([
          financeAPI.getRevenues(filters),
          financeAPI.getSummary(filters)
        ]);
        setRevenues(revenuesRes.data.revenues);
        setSummary(summaryRes.data.summary);
      } else if (activeTab === 'budgets') {
        const [budgetsRes, comparisonRes] = await Promise.all([
          financeAPI.getBudgets({ currency: filters.currency }),
          financeAPI.getBudgetComparison({
            start_date: filters.start_date,
            end_date: filters.end_date,
            currency: filters.currency
          })
        ]);
        setBudgets(budgetsRes.data.budgets);
        setBudgetComparison(comparisonRes.data.comparison);
      } else if (activeTab === 'trends') {
        const trendsRes = await financeAPI.getTrends({
          currency: filters.currency,
          months: 6
        });
        setTrends(trendsRes.data.trends);
      }
    } catch (error) {
      console.error('Error loading finance data:', error);
      reportLoadError(error, t('Error loading finance data'), { main: true });
    } finally {
      setLoading(false);
    }
  };

  // Finance works month by month by default (first to last day of the
  // month); "Custom range" shows the two dates instead.
  const [periodMode, setPeriodMode] = useState('month');
  const selectedMonth = monthOf(filters.start_date) || toLocalMonth();
  const goToMonth = (month) => {
    const { start, end } = monthBounds(month);
    setFilters({ ...filters, start_date: start, end_date: end });
  };
  const monthLabel = (month) => parseLocalDate(`${month}-01`)?.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const periodLabel = periodMode === 'month'
    ? monthLabel(selectedMonth)
    : `${parseLocalDate(filters.start_date)?.toLocaleDateString(locale) || '…'} – ${parseLocalDate(filters.end_date)?.toLocaleDateString(locale) || '…'}`;

  const renderPeriodPicker = () => (periodMode === 'month' ? (
    <div className="fin-field fin-period">
      <label className="field-label">{t('Month')}</label>
      <div className="fin-month-nav">
        <button type="button" className="btn btn--secondary btn--icon" onClick={() => goToMonth(addMonths(selectedMonth, -1))} aria-label={t('Previous month')}><ChevronLeft aria-hidden="true" /></button>
        <span className="fin-month-label">{monthLabel(selectedMonth)}</span>
        <button type="button" className="btn btn--secondary btn--icon" onClick={() => goToMonth(addMonths(selectedMonth, 1))} aria-label={t('Next month')}><ChevronRight aria-hidden="true" /></button>
      </div>
      <button type="button" className="btn btn--secondary" onClick={() => setPeriodMode('custom')}>
        {t('Custom range')}
      </button>
    </div>
  ) : (
    <>
      <div className="fin-field">
        <label className="field-label">{t('Start Date')}</label>
        <input className="input"
          type="date"
          value={filters.start_date}
          onChange={(e) => setFilters({...filters, start_date: e.target.value})}
        />
      </div>

      <div className="fin-field">
        <label className="field-label">{t('End Date')}</label>
        <input className="input"
          type="date"
          value={filters.end_date}
          onChange={(e) => setFilters({...filters, end_date: e.target.value})}
        />
      </div>

      <div className="fin-field fin-period">
        <label className="field-label">&nbsp;</label>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => { setPeriodMode('month'); goToMonth(monthOf(filters.start_date) || toLocalMonth()); }}
        >
          {t('By month')}
        </button>
      </div>
    </>
  ));

  // A record dated outside the filter, or in another currency, didn't show
  // after saving and looked lost (one revenue was entered three times).
  // Widen the filter to include it; the filters effect then reloads.
  const showCreated = (record) => {
    const next = periodMode === 'month'
      ? monthFiltersIncluding(filters, record)
      : filtersIncluding(filters, record);
    if (next === filters) loadData();
    else setFilters(next);
  };

  const openCreateExpenseModal = () => {
    setExpenseForm((form) => ({ ...form, currency: filters.currency || 'USD' }));
    setShowCreateExpenseModal(true);
  };

  const openCreateRevenueModal = () => {
    setRevenueForm((form) => ({ ...form, currency: filters.currency || 'USD' }));
    setShowCreateRevenueModal(true);
  };

  const openCreateBudgetModal = () => {
    setBudgetForm((form) => ({ ...form, currency: filters.currency || 'USD' }));
    setShowCreateBudgetModal(true);
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...expenseForm,
        category: expenseForm.category === 'CUSTOM' ? expenseForm.customCategory : expenseForm.category
      };
      await financeAPI.createExpense(submitData);
      notify(t('Expense created successfully!'), 'success');
      setShowCreateExpenseModal(false);
      resetExpenseForm();
      showCreated({ date: submitData.expense_date, currency: submitData.currency });
    } catch (error) {
      notify(error.response?.data?.message || t('Error creating expense'), 'error');
    }
  };

  const handleUpdateExpense = async (e) => {
    e.preventDefault();
    try {
      await financeAPI.updateExpense(editingExpense.id, editingExpense);
      notify(t('Expense updated!'), 'success');
      setShowEditExpenseModal(false);
      setEditingExpense(null);
      loadData();
    } catch (error) {
      notify(t('Error updating expense'), 'error');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadReceipt = async () => {
    if (!selectedFile || !selectedExpense) {
      notify(t('Please select a file'), 'warning');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('receipt', selectedFile);

      await financeAPI.uploadReceipt(selectedExpense.id, formData);
      notify(t('Receipt uploaded!'), 'success');
      setShowReceiptModal(false);
      setSelectedFile(null);
      setSelectedExpense(null);
      loadData();
    } catch (error) {
      notify(t('Error uploading receipt'), 'error');
    }
  };

  // Receipts come through the API (finance:view), like Documents: nginx no
  // longer serves /uploads/receipts/.
  const handleDownloadReceipt = async (expense) => {
    try {
      const response = await financeAPI.downloadReceipt(expense.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.data.type }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', expense.receipt_file_name || `receipt-${expense.id}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      notify(t('Error downloading receipt'), 'error');
    }
  };

  const handleApproveExpense = async (id) => {
    if (!await confirmDialog(t('Approve this expense?'))) return;
    
    try {
      await financeAPI.approveExpense(id, {});
      notify(t('Expense approved!'), 'success');
      loadData();
    } catch (error) {
      notify(error.response?.data?.message || t('Error approving expense'), 'error');
    }
  };

  const handleRejectExpense = async (id) => {
    const notes = window.prompt(t('Reason for rejection:'));
    if (!notes) return;
    
    try {
      await financeAPI.rejectExpense(id, { notes });
      notify(t('Expense rejected'), 'success');
      loadData();
    } catch (error) {
      notify(error.response?.data?.message || t('Error rejecting expense'), 'error');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!await confirmDialog(t('Delete this expense?'))) return;

    try {
      await financeAPI.deleteExpense(id);
      notify(t('Expense deleted'), 'success');
      loadData();
    } catch (error) {
      notify(t('Error deleting expense'), 'error');
    }
  };

  const handleCreateRevenue = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...revenueForm,
        category: revenueForm.category === 'CUSTOM' ? revenueForm.customCategory : revenueForm.category
      };
      await financeAPI.createRevenue(submitData);
      notify(t('Revenue created successfully!'), 'success');
      setShowCreateRevenueModal(false);
      resetRevenueForm();
      showCreated({ date: submitData.revenue_date, currency: submitData.currency });
    } catch (error) {
      notify(error.response?.data?.message || t('Error creating revenue'), 'error');
    }
  };

  const openOpeningBalanceModal = () => {
    setOpeningBalanceInput(String(cashOnHand?.opening_balance ?? 0));
    setShowOpeningBalanceModal(true);
  };

  const handleSaveOpeningBalance = async (e) => {
    e.preventDefault();
    try {
      const res = await financeAPI.setOpeningBalance({ currency: filters.currency, amount: openingBalanceInput });
      setCashOnHand(res.data);
      setShowOpeningBalanceModal(false);
      notify(t('Opening balance updated!'), 'success');
      loadData(); // the runway depends on it
    } catch (error) {
      notify(error.response?.data?.message || t('Error updating opening balance'), 'error');
    }
  };

  const openEditRevenueModal = (revenue) => {
    setEditingRevenue({
      ...revenue,
      revenue_date: toDateInputValue(revenue.date)
    });
    setShowEditRevenueModal(true);
  };

  const handleUpdateRevenue = async (e) => {
    e.preventDefault();
    try {
      const { id, category, amount, currency, description, revenue_date, payment_method } = editingRevenue;
      await financeAPI.updateRevenue(id, { category, amount, currency, description, revenue_date, payment_method });
      notify(t('Revenue updated!'), 'success');
      setShowEditRevenueModal(false);
      setEditingRevenue(null);
      // A new date or currency can take it out of the current filter.
      showCreated({ date: revenue_date, currency });
    } catch (error) {
      notify(error.response?.data?.message || t('Error updating revenue'), 'error');
    }
  };

  const handleDeleteRevenue = async (id) => {
    if (!await confirmDialog(t('Delete this revenue?'))) return;

    try {
      await financeAPI.deleteRevenue(id);
      notify(t('Revenue deleted'), 'success');
      loadData();
    } catch (error) {
      notify(t('Error deleting revenue'), 'error');
    }
  };

  const resetRevenueForm = () => {
    setRevenueForm({
      category: 'Sales',
      amount: '',
      currency: filters.currency || 'USD',
      description: '',
      revenue_date: toLocalISODate(new Date()),
      payment_method: '',
      customCategory: ''
    });
  };

  const handleCreateBudget = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...budgetForm,
        category: budgetForm.category === 'CUSTOM' ? budgetForm.customCategory : budgetForm.category
      };
      await financeAPI.createBudget(submitData);
      notify(t('Budget created!'), 'success');
      setShowCreateBudgetModal(false);
      resetBudgetForm();
      // Budgets are listed by currency only, so only the currency can hide it.
      showCreated({ currency: submitData.currency });
    } catch (error) {
      notify(error.response?.data?.message || t('Error creating budget'), 'error');
    }
  };

  const handleUpdateBudget = async (e) => {
    e.preventDefault();
    try {
      await financeAPI.updateBudget(editingBudget.id, editingBudget);
      notify(t('Budget updated!'), 'success');
      setShowEditBudgetModal(false);
      setEditingBudget(null);
      loadData();
    } catch (error) {
      notify(t('Error updating budget'), 'error');
    }
  };

  const handleDeleteBudget = async (id) => {
    if (!await confirmDialog(t('Delete this budget?'))) return;

    try {
      await financeAPI.deleteBudget(id);
      notify(t('Budget deleted'), 'success');
      loadData();
    } catch (error) {
      notify(t('Error deleting budget'), 'error');
    }
  };

  const openEditExpenseModal = (expense) => {
    setEditingExpense({
      ...expense,
      expense_date: toDateInputValue(expense.date)
    });
    setShowEditExpenseModal(true);
  };

  const openEditBudgetModal = (budget) => {
    setEditingBudget({
      ...budget,
      start_date: toDateInputValue(budget.start_date),
      end_date: budget.end_date ? toDateInputValue(budget.end_date) : budget.end_date
    });
    setShowEditBudgetModal(true);
  };

  const openReceiptModal = (expense) => {
    setSelectedExpense(expense);
    setShowReceiptModal(true);
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      category: 'Office Supplies',
      subcategory: '',
      amount: '',
      currency: filters.currency || 'USD',
      description: '',
      expense_date: toLocalISODate(new Date()),
      tax_deductible: false,
      category_type: 'operational',
      budget_id: null,
      customCategory: ''
    });
  };

  const resetBudgetForm = () => {
    setBudgetForm({
      category: 'Office Supplies',
      currency: filters.currency || 'USD',
      amount: '',
      period_type: 'monthly',
      start_date: toLocalISODate(new Date()),
      end_date: '',
      customCategory: ''
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: t('Pending'),
      approved: t('Approved'),
      rejected: t('Rejected')
    };
    const known = badges[status] ? status : 'pending';

    return <StatusBadge status={known}>{badges[known]}</StatusBadge>;
  };

  // Chart colours and axes from the design tokens (utils/chartTheme.js),
  // the same as the dashboard's chart.js charts.
  const colors = chartColors();
  const COLORS = colors.series;
  const axisProps = { tick: { fill: colors.axis, fontSize: 13, fontFamily: CHART_FONT }, stroke: colors.grid };
  const grid = <CartesianGrid stroke={colors.grid} vertical={false} />;

  const canDelete = user?.role === 'super_admin';
  const canCreate = ['super_admin', 'finance_manager'].includes(user?.role);
  // Same rule as the backend's requirePermission('finance', 'update').
  const financePermission = Array.isArray(user?.permissions)
    ? user.permissions.find((p) => p.resource === 'finance') || {}
    : {};
  const canUpdateFinance = user?.role === 'super_admin' || financePermission.can_edit === true;
  const canApprove = ['super_admin', 'finance_manager'].includes(user?.role);

  return (
    <div className="finance-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Finance Management')}</h1>
          <p className="page-sub">{t('Track expenses, budgets and financial performance')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabbar">
        <button
          className={`tabbar__tab${activeTab === 'expenses' ? ' is-active' : ''}`}
          onClick={() => switchTab('expenses')}
        >
          <Receipt aria-hidden="true" /> {t('Expenses')}
        </button>
        <button
          className={`tabbar__tab${activeTab === 'revenues' ? ' is-active' : ''}`}
          onClick={() => switchTab('revenues')}
        >
          <Banknote aria-hidden="true" /> {t('Revenues')}
        </button>
        <button
          className={`tabbar__tab${activeTab === 'budgets' ? ' is-active' : ''}`}
          onClick={() => switchTab('budgets')}
        >
          <Target aria-hidden="true" /> {t('Budgets')}
        </button>
        <button
          className={`tabbar__tab${activeTab === 'trends' ? ' is-active' : ''}`}
          onClick={() => switchTab('trends')}
        >
          <ChartLine aria-hidden="true" /> {t('Trends')}
        </button>
      </div>

      {/* Quick Stats Dashboard */}
      {activeTab === 'expenses' && burnRate && (
        <div className="kpi-grid">
          <div className="kpi">
            <div className="kpi__label"><span className="kpi__icon"><Flame aria-hidden="true" /></span>{t('Burn Rate')}</div>
            <p className="kpi__value">
              {formatCurrency(burnRate.burn_rate, filters.currency)}/mo
            </p>
            {/* No trend without a last month to compare with (it read "NaN%") */}
            {Number.isFinite(parseFloat(burnRate.trend_percentage)) && (
            <div className={`kpi__foot fin-trend ${parseFloat(burnRate.trend_percentage) > 0 ? 'is-up' : 'is-down'}`}>
              {parseFloat(burnRate.trend_percentage) > 0 ? '↑' : '↓'} {Math.abs(parseFloat(burnRate.trend_percentage)).toFixed(1)}% {t('vs last month')}
            </div>
            )}
          </div>

          {runway && (
            <div className="kpi">
              <div className="kpi__label"><span className="kpi__icon"><Hourglass aria-hidden="true" /></span>{t('Runway')}</div>
              <p className="kpi__value">{runway.runway_months} {t('months')}</p>
              <div className="kpi__foot">
                {t('Until')} {parseLocalDate(runway.projected_depletion_date)?.toLocaleDateString(locale, {
                  month: 'short',
                  year: 'numeric'
                })}
              </div>
            </div>
          )}

          <div className="kpi">
            <div className="kpi__label"><span className="kpi__icon"><TriangleAlert aria-hidden="true" /></span>{t('Budget Alerts')}</div>
            <p className="kpi__value">
              {budgetComparison.filter(b => parseFloat(b.usage_percentage) > 80).length}
            </p>
            <div className="kpi__foot">{t('Categories over 80%')}</div>
          </div>

          <div className="kpi">
            <div className="kpi__label"><span className="kpi__icon"><Coins aria-hidden="true" /></span>{t('Cash on Hand')}</div>
            <p className="kpi__value">
              {formatCurrency(cashOnHand?.cash_on_hand ?? 0, filters.currency)}
            </p>
            <div className="kpi__foot fin-kpi-foot">
              {t('Opening balance: {amount}', { amount: formatCurrency(cashOnHand?.opening_balance ?? 0, filters.currency) })}
              {canUpdateFinance && (
                <button
                  type="button"
                  className="btn btn--ghost fin-inline-btn"
                  onClick={openOpeningBalanceModal}
                >
                  <Pencil aria-hidden="true" /> {t('Edit')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Currency Switcher */}
      <div className="fin-currency">
        <span className="fin-currency__label">{t('Currency:')}</span>
        <div className="fin-segmented" role="group" aria-label={t('Currency:')}>
          <button
            type="button"
            className={filters.currency === 'USD' ? 'is-active' : ''}
            aria-pressed={filters.currency === 'USD'}
            onClick={() => setFilters({...filters, currency: 'USD'})}
          >
            USD
          </button>
          <button
            type="button"
            className={filters.currency === 'SRD' ? 'is-active' : ''}
            aria-pressed={filters.currency === 'SRD'}
            onClick={() => setFilters({...filters, currency: 'SRD'})}
          >
            SRD
          </button>
        </div>
      </div>

      {/* EXPENSES TAB */}
      {activeTab === 'expenses' && (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="kpi-grid">
              <div className="kpi">
                <h3 className="kpi__label">{t('Total Expenses (Approved)')}</h3>
                <p className="kpi__value">
                  {formatCurrency(summary.total_expenses, filters.currency)}
                </p>
                <span className="kpi__foot">
                  {t('{count} records', { count: summary.expense_count })}
                </span>
              </div>
              <div className="kpi">
                <h3 className="kpi__label">{t('Pending Expenses')}</h3>
                <p className="kpi__value">
                  {formatCurrency(summary.pending_expenses, filters.currency)}
                </p>
                <span className="kpi__foot">
                  {t('Awaiting approval')}
                </span>
              </div>
              <div className={`kpi ${summary.net_profit >= 0 ? 'fin-kpi--positive' : 'fin-kpi--negative'}`}>
                <h3 className="kpi__label">{t('Profit')} · {periodLabel}</h3>
                <p className="kpi__value">
                  {formatCurrency(summary.net_profit, filters.currency)}
                </p>
                <span className="kpi__foot">
                  {t('Revenue minus expenses')}
                </span>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="fin-filters">
            {renderPeriodPicker()}

            <div className="fin-field">
              <label className="field-label">{t('Category')}</label>
              <select className="select"
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
              >
                <option value="">{t('All Categories')}</option>
                {Object.keys(EXPENSE_CATEGORIES).map(cat => (
                  <option key={cat} value={cat}>{t(cat)}</option>
                ))}
              </select>
            </div>

            <div className="fin-field">
              <label className="field-label">{t('Status')}</label>
              <select className="select"
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="">{t('All Status')}</option>
                <option value="pending">{t('Pending')}</option>
                <option value="approved">{t('Approved')}</option>
                <option value="rejected">{t('Rejected')}</option>
              </select>
            </div>

            {canCreate && (
              <button onClick={openCreateExpenseModal} className="btn btn--primary">
                <Plus aria-hidden="true" /> {t('Add Expense')}
              </button>
            )}
          </div>

          {/* EXPENSE HISTORY SECTION */}
          <div className="fin-section-head">
            <h3 className="fin-section-title"><History aria-hidden="true" /> {t('Expense History')}</h3>
          </div>

          <div className="fin-content">
            {loading ? (
              <div className="empty">{t('Loading expense history...')}</div>
            ) : expenses.length === 0 ? (
              <div className="empty">
                <p>{t('No expenses found for the selected period')}</p>
                {canCreate && (
                  <button onClick={openCreateExpenseModal} className="btn btn--primary">
                    {t('Create First Expense')}
                  </button>
                )}
              </div>
            ) : (
              <div className="fin-history">
                {expenses.map((expense, index) => (
                  <div key={expense.id} className="fin-history-item">
                    <div className="fin-timeline">
                      <div className="fin-timeline-dot"></div>
                      {index < expenses.length - 1 && <div className="fin-timeline-line"></div>}
                    </div>
                    
                    <div className="fin-record">
                      <div className="fin-record__head">
                        <div className="fin-record__when">
                          <div className="fin-record__date">
                            {parseLocalDate(expense.date)?.toLocaleDateString(locale, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                          <div className="fin-record__category">{t(expense.category)}</div>
                        </div>
                        
                        <div className="fin-record__amount-status">
                          <div className="fin-record__amount">
                            {formatCurrency(expense.amount, expense.currency)}
                          </div>
                          {getStatusBadge(expense.status)}
                        </div>
                      </div>

                      {expense.description && (
                        <div className="fin-record__description">
                          <strong>{t('Description:')}</strong> {expense.description}
                        </div>
                      )}

                      {expense.subcategory && (
                        <div className="fin-record__tag">
                          <Tag aria-hidden="true" /> {t(expense.subcategory)}
                        </div>
                      )}

                      <div className="fin-record__details">
                        <div className="fin-record__detail">
                          <span className="fin-record__label">{t('Type:')}</span>
                          <span className="fin-record__value">{t(expense.category_type || 'operational')}</span>
                        </div>
                        {expense.payment_method && (
                          <div className="fin-record__detail">
                            <span className="fin-record__label">{t('Payment:')}</span>
                            <span className="fin-record__value">{t(expense.payment_method)}</span>
                          </div>
                        )}
                        {expense.vendor_name && (
                          <div className="fin-record__detail">
                            <span className="fin-record__label">{t('Vendor:')}</span>
                            <span className="fin-record__value">{expense.vendor_name}</span>
                          </div>
                        )}
                        <div className="fin-record__detail">
                          <span className="fin-record__label">{t('Tax Deductible:')}</span>
                          <span className="fin-record__value">{expense.tax_deductible ? t('Yes') : t('No')}</span>
                        </div>
                      </div>

                      {expense.receipt_url && (
                        <div className="fin-record__receipt">
                          <button
                            type="button"
                            onClick={() => handleDownloadReceipt(expense)}
                            className="btn btn--ghost fin-receipt-link"
                          >
                            <Paperclip aria-hidden="true" /> {t('View Receipt')}
                          </button>
                        </div>
                      )}

                      <div className="fin-record__actions">
                        {!expense.receipt_url && (
                          <button
                            onClick={() => openReceiptModal(expense)}
                            className="btn btn--secondary fin-upload"
                            title={t('Upload Receipt')}
                          >
                            <Paperclip aria-hidden="true" /> {t('Upload Receipt')}
                          </button>
                        )}

                        {canApprove && expense.status === 'pending' && (
                          <>
                            {/* Nobody approves an expense they created (the server refuses it). */}
                            {expense.created_by !== user?.id && (
                            <button
                              onClick={() => handleApproveExpense(expense.id)}
                              className="btn btn--primary fin-approve"
                            >
                              <Check aria-hidden="true" /> {t('Approve')}
                            </button>
                            )}
                            <button
                              onClick={() => handleRejectExpense(expense.id)}
                              className="btn btn--secondary fin-reject"
                            >
                              <X aria-hidden="true" /> {t('Reject')}
                            </button>
                          </>
                        )}

                        {canCreate && (
                          <button
                            onClick={() => openEditExpenseModal(expense)}
                            className="btn btn--secondary fin-edit"
                          >
                            <Pencil aria-hidden="true" /> {t('Edit')}
                          </button>
                        )}

                        {canDelete && (
                          <button
                            onClick={() => handleDeleteExpense(expense.id)}
                            className="btn btn--secondary fin-delete"
                          >
                            <Trash2 aria-hidden="true" /> {t('Delete')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* REVENUES TAB */}
      {activeTab === 'revenues' && (
        <>
          {/* Summary Cards */}
          <div className="kpi-grid">
            <div className="kpi">
              <h3 className="kpi__label">{t('Total Revenues')}</h3>
              <p className="kpi__value">
                {formatCurrency(
                  revenues
                    .filter(r => r.status === 'approved')
                    .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0),
                  filters.currency
                )}
              </p>
              <span className="kpi__foot">
                {t('{count} records', { count: revenues.filter(r => r.status === 'approved').length })}
              </span>
            </div>
            <div className="kpi">
              <h3 className="kpi__label">{t('Pending Revenues')}</h3>
              <p className="kpi__value">
                {formatCurrency(
                  revenues
                    .filter(r => r.status === 'pending')
                    .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0),
                  filters.currency
                )}
              </p>
              <span className="kpi__foot">
                {t('Awaiting confirmation')}
              </span>
            </div>
            <div className={`kpi ${(summary?.net_profit ?? 0) >= 0 ? 'fin-kpi--positive' : 'fin-kpi--negative'}`}>
              <h3 className="kpi__label">{t('Profit')} · {periodLabel}</h3>
              <p className="kpi__value">
                {formatCurrency(summary?.net_profit ?? 0, filters.currency)}
              </p>
              <span className="kpi__foot">
                {t('Revenue minus expenses')}
              </span>
            </div>
          </div>

          {/* Filters */}
          <div className="fin-filters">
            {renderPeriodPicker()}

            <div className="fin-field">
              <label className="field-label">{t('Category')}</label>
              <select className="select"
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
              >
                <option value="">{t('All Categories')}</option>
                {Object.keys(REVENUE_CATEGORIES).map(cat => (
                  <option key={cat} value={cat}>{t(cat)}</option>
                ))}
              </select>
            </div>

            {canCreate && (
              <button onClick={openCreateRevenueModal} className="btn btn--primary">
                <Plus aria-hidden="true" /> {t('Add Revenue')}
              </button>
            )}
          </div>

          {/* REVENUE HISTORY */}
          <div className="fin-section-head">
            <h3 className="fin-section-title"><History aria-hidden="true" /> {t('Revenue History')}</h3>
          </div>

          <div className="fin-content">
            {loading ? (
              <div className="empty">{t('Loading revenue history...')}</div>
            ) : revenues.length === 0 ? (
              <div className="empty">
                <p>{t('No revenues found for the selected period')}</p>
                {canCreate && (
                  <button onClick={openCreateRevenueModal} className="btn btn--primary">
                    {t('Create First Revenue')}
                  </button>
                )}
              </div>
            ) : (
              <div className="fin-history">
                {revenues.map((revenue, index) => (
                  <div key={revenue.id} className="fin-history-item">
                    <div className="fin-timeline">
                      <div className="fin-timeline-dot"></div>
                      {index < revenues.length - 1 && <div className="fin-timeline-line"></div>}
                    </div>
                    
                    <div className="fin-record fin-record--revenue">
                      <div className="fin-record__head">
                        <div className="fin-record__when">
                          <div className="fin-record__date">
                            {parseLocalDate(revenue.date)?.toLocaleDateString(locale, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                          <div className="fin-record__category">{t(revenue.category)}</div>
                        </div>
                        
                        <div className="fin-record__amount-status">
                          <div className="fin-record__amount">
                            {formatCurrency(revenue.amount, revenue.currency)}
                          </div>
                          {getStatusBadge(revenue.status)}
                        </div>
                      </div>

                      {revenue.description && (
                        <div className="fin-record__description">
                          <strong>{t('Description:')}</strong> {revenue.description}
                        </div>
                      )}

                      <div className="fin-record__details">
                        {revenue.payment_method && (
                          <div className="fin-record__detail">
                            <span className="fin-record__label">{t('Payment:')}</span>
                            <span className="fin-record__value">{t(revenue.payment_method)}</span>
                          </div>
                        )}
                      </div>

                      <div className="fin-record__actions">
                        {canCreate && (
                          <button
                            onClick={() => openEditRevenueModal(revenue)}
                            className="btn btn--secondary fin-edit"
                          >
                            <Pencil aria-hidden="true" /> {t('Edit')}
                          </button>
                        )}

                        {canDelete && (
                          <button
                            onClick={() => handleDeleteRevenue(revenue.id)}
                            className="btn btn--secondary fin-delete"
                          >
                            <Trash2 aria-hidden="true" /> {t('Delete')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* BUDGETS TAB */}
      {activeTab === 'budgets' && (
        <>
          {/* Budget vs Actual Chart */}
          {budgetComparison.length > 0 && (
            <ErrorBoundary>
              <div className="fin-chart">
                <h3 className="fin-section-title"><ChartColumn aria-hidden="true" /> {t('Budget vs Actual Spending')}</h3>
                <ResponsiveContainer width="100%" height={360}>
                  <BarChart data={budgetComparison} margin={{ top: 20, right: 20, left: 0, bottom: 8 }}>
                    {grid}
                    <XAxis dataKey="category" {...axisProps} />
                    <YAxis
                      {...axisProps}
                      domain={[0, 'auto']}
                      tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}K` : value}
                    />
                    <Tooltip formatter={(value) => value.toLocaleString()} />
                    <Legend wrapperStyle={{ fontFamily: CHART_FONT, fontSize: 13 }} />
                    <Bar dataKey="budget_amount" fill={colors.steel} name={t('Budget')} maxBarSize={40} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="actual_amount" fill={colors.primary} name={t('Actual')} maxBarSize={40} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ErrorBoundary>
          )}

          {/* Budget Usage Cards */}
          <div className="fin-budget-grid">
            {budgetComparison.map((item, index) => {
              const usagePercentage = parseFloat(item.usage_percentage || 0);
              const remaining = parseFloat(item.remaining || 0);
              
              return (
                <div key={index} className={`fin-budget ${usagePercentage > 100 ? 'is-over' : usagePercentage > 80 ? 'is-warn' : 'is-ok'}`}>
                  <div className="fin-budget__head">
                    <h4>{item.category}</h4>
                    <span className="fin-budget__pct">
                      {usagePercentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="fin-budget__bar">
                    <div 
                      className="fin-budget__fill"
                      style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                    />
                  </div>
                  <div className="fin-budget__details">
                    <div>
                      <span className="fin-budget__label">{t('Budget:')}</span>
                      <span className="fin-budget__value">{formatCurrency(item.budget_amount, item.currency)}</span>
                    </div>
                    <div>
                      <span className="fin-budget__label">{t('Spent (Approved + Pending):')}</span>
                      <span className="fin-budget__value fin-budget__value--spent">
                        {formatCurrency(item.actual_amount, item.currency)}
                      </span>
                    </div>
                    <div>
                      <span className="fin-budget__label">{t('Remaining:')}</span>
                      <span className={`fin-budget__value fin-budget__value--remaining ${remaining < 0 ? 'is-negative' : 'is-positive'}`}>
                        {formatCurrency(remaining, item.currency)}
                      </span>
                    </div>
                  </div>
                  {usagePercentage > 100 && (
                    <div className="fin-budget__note is-over">
                      <TriangleAlert aria-hidden="true" /> {t('Over budget by {amount}', { amount: formatCurrency(Math.abs(remaining), item.currency) })}
                    </div>
                  )}
                  {usagePercentage > 80 && usagePercentage <= 100 && (
                    <div className="fin-budget__note is-warn">
                      <TriangleAlert aria-hidden="true" /> {t('{pct}% remaining', { pct: (100 - usagePercentage).toFixed(1) })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Budgets Table */}
          <div className="fin-section-head">
            <h3 className="fin-section-title"><List aria-hidden="true" /> {t('All Budgets')}</h3>
            {canCreate && (
              <button onClick={openCreateBudgetModal} className="btn btn--primary">
                <Plus aria-hidden="true" /> {t('Add Budget')}
              </button>
            )}
          </div>

          <div className="fin-content">
            {loading ? (
              <div className="empty">{t('Loading budgets...')}</div>
            ) : budgets.length === 0 ? (
              <div className="empty">
                <p>{t('No budgets found')}</p>
                {canCreate && (
                  <button onClick={openCreateBudgetModal} className="btn btn--primary">
                    {t('Create First Budget')}
                  </button>
                )}
              </div>
            ) : (
              <div className="fin-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('Category')}</th>
                      <th className="num">{t('Amount')}</th>
                      <th>{t('Currency')}</th>
                      <th>{t('Period')}</th>
                      <th>{t('Start Date')}</th>
                      <th>{t('End Date')}</th>
                      <th>{t('Status')}</th>
                      <th>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {budgets.map(budget => (
                      <tr key={budget.id}>
                        <td><strong>{budget.category}</strong></td>
                        <td className="num"><strong>{formatCurrency(budget.amount, budget.currency)}</strong></td>
                        <td>{budget.currency}</td>
                        <td>{t(budget.period_type)}</td>
                        <td>{parseLocalDate(budget.start_date)?.toLocaleDateString(locale)}</td>
                        <td>{budget.end_date ? parseLocalDate(budget.end_date)?.toLocaleDateString(locale) : '-'}</td>
                        <td>
                          <StatusBadge status={budget.is_active ? 'active' : 'inactive'}>
                            {budget.is_active ? t('Active') : t('Inactive')}
                          </StatusBadge>
                        </td>
                        <td>
                          <div className="fin-actions">
                            {canCreate && (
                              <button
                                onClick={() => openEditBudgetModal(budget)}
                                className="btn btn--secondary btn--icon"
                                title={t('Edit')}
                                aria-label={t('Edit')}
                              >
                                <Pencil aria-hidden="true" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => handleDeleteBudget(budget.id)}
                                className="btn btn--secondary btn--icon btn--danger-text"
                                title={t('Delete')}
                                aria-label={t('Delete')}
                              >
                                <Trash2 aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* TRENDS TAB */}
      {activeTab === 'trends' && (
        <>
          {trends.length > 0 ? (
            <>
              {/* Line Chart - Trends */}
              <ErrorBoundary>
                <div className="fin-chart">
                  <h3 className="fin-section-title"><ChartLine aria-hidden="true" /> {t('Financial Trends (Last 6 Months)')}</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={[...trends].reverse()} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
                      {grid}
                      <XAxis dataKey="month" {...axisProps} />
                      <YAxis {...axisProps} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value, filters.currency)}
                      />
                      <Legend wrapperStyle={{ fontFamily: CHART_FONT, fontSize: 13 }} />
                      <Line type="monotone" dataKey="expenses" stroke={colors.steel} name={t('Expenses')} strokeWidth={2.5} />
                      <Line type="monotone" dataKey="revenues" stroke={colors.primary} name={t('Revenues')} strokeWidth={2.5} />
                      <Line type="monotone" dataKey="net" stroke={colors.dark} name={t('Net Profit')} strokeWidth={2.5} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </ErrorBoundary>

              {/* Pie Chart - Expense Categories */}
              <ErrorBoundary>
                <div className="fin-chart">
                  <h3 className="fin-section-title"><ChartPie aria-hidden="true" /> {t('Expense Distribution')}</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={expenses.reduce((acc, exp) => {
                          const existing = acc.find(item => item.name === exp.category);
                          if (existing) {
                            existing.value += parseFloat(exp.amount);
                          } else {
                            acc.push({ name: exp.category, value: parseFloat(exp.amount) });
                          }
                          return acc;
                        }, [])}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius="60%"
                        fill={colors.primary}
                        dataKey="value"
                      >
                        {expenses.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value, filters.currency)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </ErrorBoundary>
            </>
          ) : (
            <div className="empty">
              <p>{t('No trend data available')}</p>
            </div>
          )}
        </>
      )}

      {/* ========== CREATE EXPENSE MODAL ========== */}
      {showCreateExpenseModal && (
        <Modal open title={t('Create Expense')} onClose={() => setShowCreateExpenseModal(false)} size="md">

            <form onSubmit={handleCreateExpense} className="fin-dialog">
              <div className="fin-form-row">
                <div className="fin-field">
                  <label className="field-label">{t('Category *')}</label>
                  <select className="select"
                    value={expenseForm.category === 'CUSTOM' || !Object.keys(EXPENSE_CATEGORIES).includes(expenseForm.category) ? 'CUSTOM' : expenseForm.category}
                    onChange={(e) => {
                      if (e.target.value === 'CUSTOM') {
                        setExpenseForm({...expenseForm, category: 'CUSTOM', subcategory: '', customCategory: ''});
                      } else {
                        setExpenseForm({...expenseForm, category: e.target.value, subcategory: '', customCategory: ''});
                      }
                    }}
                    required
                  >
                    {Object.keys(EXPENSE_CATEGORIES).map(cat => (
                      <option key={cat} value={cat}>{t(cat)}</option>
                    ))}
                    <option value="CUSTOM">{t('Custom Category...')}</option>
                  </select>
                  
                  {expenseForm.category === 'CUSTOM' && (
                    <input className="input"
                      type="text"
                      placeholder={t('Enter custom category name...')}
                      value={expenseForm.customCategory}
                      onChange={(e) => setExpenseForm({...expenseForm, customCategory: e.target.value})}
                      style={{marginTop: '10px'}}
                      required
                    />
                  )}
                </div>

                <div className="fin-field">
                  <label className="field-label">{t('Subcategory')}</label>
                  <select className="select"
                    value={expenseForm.subcategory}
                    onChange={(e) => setExpenseForm({...expenseForm, subcategory: e.target.value})}
                    disabled={expenseForm.category === 'CUSTOM'}
                  >
                    <option value="">{t('Select subcategory...')}</option>
                    {expenseForm.category !== 'CUSTOM' && EXPENSE_CATEGORIES[expenseForm.category]?.map(sub => (
                      <option key={sub} value={sub}>{t(sub)}</option>
                    ))}
                  </select>
                </div>

                <div className="fin-field">
                  <label className="field-label">{t('Currency *')}</label>
                  <select className="select"
                    value={expenseForm.currency}
                    onChange={(e) => setExpenseForm({...expenseForm, currency: e.target.value})}
                    required
                  >
                    <option value="USD">USD</option>
                    <option value="SRD">SRD</option>
                  </select>
                </div>
              </div>

              <div className="fin-form-row">
                <div className="fin-field">
                  <label className="field-label">{t('Amount *')}</label>
                  <input className="input"
                    type="number"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="fin-field">
                  <label className="field-label">{t('Date *')}</label>
                  <input className="input"
                    type="date"
                    value={expenseForm.expense_date}
                    onChange={(e) => setExpenseForm({...expenseForm, expense_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="fin-field">
                <label className="field-label">{t('Description')}</label>
                <textarea className="textarea"
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                  placeholder={t('Add expense details...')}
                />
              </div>

              <div className="fin-field">
                <label className="fin-check">
                  <input
                    type="checkbox"
                    checked={expenseForm.tax_deductible}
                    onChange={(e) => setExpenseForm({...expenseForm, tax_deductible: e.target.checked})}
                  />
                  <span>{t('Tax Deductible')}</span>
                </label>
              </div>

              <div className="fin-form-actions">
                <button type="button" onClick={() => setShowCreateExpenseModal(false)} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  {t('Create Expense')}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* ========== CREATE REVENUE MODAL ========== */}
      {showCreateRevenueModal && (
        <Modal open title={t('Create Revenue')} onClose={() => setShowCreateRevenueModal(false)} size="md">

            <form onSubmit={handleCreateRevenue} className="fin-dialog">
              <div className="fin-form-row">
                <div className="fin-field">
                  <label className="field-label">{t('Category *')}</label>
                  <select className="select"
                    value={revenueForm.category === 'CUSTOM' || !Object.keys(REVENUE_CATEGORIES).includes(revenueForm.category) ? 'CUSTOM' : revenueForm.category}
                    onChange={(e) => {
                      if (e.target.value === 'CUSTOM') {
                        setRevenueForm({...revenueForm, category: 'CUSTOM', customCategory: ''});
                      } else {
                        setRevenueForm({...revenueForm, category: e.target.value, customCategory: ''});
                      }
                    }}
                    required
                  >
                    {Object.entries(REVENUE_CATEGORIES).map(([key, label]) => (
                      <option key={key} value={key}>{t(label)}</option>
                    ))}
                    <option value="CUSTOM">{t('Custom Category...')}</option>
                  </select>
                  
                  {revenueForm.category === 'CUSTOM' && (
                    <input className="input"
                      type="text"
                      placeholder={t('Enter custom category name...')}
                      value={revenueForm.customCategory}
                      onChange={(e) => setRevenueForm({...revenueForm, customCategory: e.target.value})}
                      style={{marginTop: '10px'}}
                      required
                    />
                  )}
                </div>

                <div className="fin-field">
                  <label className="field-label">{t('Currency *')}</label>
                  <select className="select"
                    value={revenueForm.currency}
                    onChange={(e) => setRevenueForm({...revenueForm, currency: e.target.value})}
                    required
                  >
                    <option value="USD">USD</option>
                    <option value="SRD">SRD</option>
                  </select>
                </div>
              </div>

              <div className="fin-form-row">
                <div className="fin-field">
                  <label className="field-label">{t('Amount *')}</label>
                  <input className="input"
                    type="number"
                    value={revenueForm.amount}
                    onChange={(e) => setRevenueForm({...revenueForm, amount: e.target.value})}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="fin-field">
                  <label className="field-label">{t('Date *')}</label>
                  <input className="input"
                    type="date"
                    value={revenueForm.revenue_date}
                    onChange={(e) => setRevenueForm({...revenueForm, revenue_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="fin-field">
                <label className="field-label">{t('Payment Method')}</label>
                <select className="select"
                  value={revenueForm.payment_method}
                  onChange={(e) => setRevenueForm({...revenueForm, payment_method: e.target.value})}
                >
                  <option value="">{t('Select method...')}</option>
                  <option value="Bank Transfer">{t('Bank Transfer')}</option>
                  <option value="Cash">{t('Cash')}</option>
                  <option value="Credit Card">{t('Credit Card')}</option>
                  <option value="Check">{t('Check')}</option>
                  <option value="Other">{t('Other')}</option>
                </select>
              </div>

              <div className="fin-field">
                <label className="field-label">{t('Description')}</label>
                <textarea className="textarea"
                  value={revenueForm.description}
                  onChange={(e) => setRevenueForm({...revenueForm, description: e.target.value})}
                  placeholder={t('Add revenue details...')}
                />
              </div>

              <div className="fin-form-actions">
                <button type="button" onClick={() => setShowCreateRevenueModal(false)} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  {t('Create Revenue')}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* ========== CREATE BUDGET MODAL ========== */}
      {showCreateBudgetModal && (
        <Modal open title={t('Create Budget')} onClose={() => setShowCreateBudgetModal(false)} size="md">

            <form onSubmit={handleCreateBudget} className="fin-dialog">
              <div className="fin-form-row">
                <div className="fin-field">
                  <label className="field-label">{t('Category *')}</label>
                  <select className="select"
                    value={budgetForm.category === 'CUSTOM' || !Object.keys(EXPENSE_CATEGORIES).includes(budgetForm.category) ? 'CUSTOM' : budgetForm.category}
                    onChange={(e) => {
                      if (e.target.value === 'CUSTOM') {
                        setBudgetForm({...budgetForm, category: 'CUSTOM', customCategory: ''});
                      } else {
                        setBudgetForm({...budgetForm, category: e.target.value, customCategory: ''});
                      }
                    }}
                    required
                  >
                    {Object.keys(EXPENSE_CATEGORIES).map(cat => (
                      <option key={cat} value={cat}>{t(cat)}</option>
                    ))}
                    <option value="CUSTOM">{t('Custom Category...')}</option>
                  </select>
                  
                  {budgetForm.category === 'CUSTOM' && (
                    <input className="input"
                      type="text"
                      placeholder={t('Enter custom category name...')}
                      value={budgetForm.customCategory}
                      onChange={(e) => setBudgetForm({...budgetForm, customCategory: e.target.value})}
                      style={{marginTop: '10px'}}
                      required
                    />
                  )}
                </div>

                <div className="fin-field">
                  <label className="field-label">{t('Currency *')}</label>
                  <select className="select"
                    value={budgetForm.currency}
                    onChange={(e) => setBudgetForm({...budgetForm, currency: e.target.value})}
                    required
                  >
                    <option value="USD">USD</option>
                    <option value="SRD">SRD</option>
                  </select>
                </div>
              </div>

              <div className="fin-form-row">
                <div className="fin-field">
                  <label className="field-label">{t('Amount *')}</label>
                  <input className="input"
                    type="number"
                    value={budgetForm.amount}
                    onChange={(e) => setBudgetForm({...budgetForm, amount: e.target.value})}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="fin-field">
                  <label className="field-label">{t('Period Type *')}</label>
                  <select className="select"
                    value={budgetForm.period_type}
                    onChange={(e) => setBudgetForm({...budgetForm, period_type: e.target.value})}
                    required
                  >
                    <option value="monthly">{t('Monthly')}</option>
                    <option value="quarterly">{t('Quarterly')}</option>
                    <option value="yearly">{t('Yearly')}</option>
                  </select>
                </div>
              </div>

              <div className="fin-form-row">
                <div className="fin-field">
                  <label className="field-label">{t('Start Date *')}</label>
                  <input className="input"
                    type="date"
                    value={budgetForm.start_date}
                    onChange={(e) => setBudgetForm({...budgetForm, start_date: e.target.value})}
                    required
                  />
                </div>

                <div className="fin-field">
                  <label className="field-label">{t('End Date')}</label>
                  <input className="input"
                    type="date"
                    value={budgetForm.end_date}
                    onChange={(e) => setBudgetForm({...budgetForm, end_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="fin-form-actions">
                <button type="button" onClick={() => setShowCreateBudgetModal(false)} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  {t('Create Budget')}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* EDIT EXPENSE MODAL */}
      {showEditExpenseModal && editingExpense && (
        <Modal open title={t('Edit Expense')} onClose={() => setShowEditExpenseModal(false)} size="md">

            <form onSubmit={handleUpdateExpense} className="fin-dialog">
              <div className="fin-info">
                <p><strong>{t('Category:')}</strong> {t(editingExpense.category)}</p>
                {editingExpense.subcategory && (
                  <p><strong>{t('Subcategory:')}</strong> {t(editingExpense.subcategory)}</p>
                )}
                <p><strong>{t('Current Status:')}</strong> {t(editingExpense.status)}</p>
              </div>

              <div className="fin-form-row">
                <div className="fin-field">
                  <label className="field-label">{t('Amount *')}</label>
                  <input className="input"
                    type="number"
                    value={editingExpense.amount}
                    onChange={(e) => setEditingExpense({...editingExpense, amount: e.target.value})}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="fin-field">
                  <label className="field-label">{t('Date *')}</label>
                  <input className="input"
                    type="date"
                    value={editingExpense.expense_date}
                    onChange={(e) => setEditingExpense({...editingExpense, expense_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="fin-field">
                <label className="field-label">{t('Description')}</label>
                <textarea className="textarea"
                  value={editingExpense.description || ''}
                  onChange={(e) => setEditingExpense({...editingExpense, description: e.target.value})}
                />
              </div>

              <div className="fin-field">
                <label className="fin-check">
                  <input
                    type="checkbox"
                    checked={editingExpense.tax_deductible}
                    onChange={(e) => setEditingExpense({...editingExpense, tax_deductible: e.target.checked})}
                  />
                  <span>{t('Tax Deductible')}</span>
                </label>
              </div>

              <div className="fin-form-actions">
                <button type="button" onClick={() => setShowEditExpenseModal(false)} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  {t('Update Expense')}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {showOpeningBalanceModal && (
        <Modal open title={<>{t('Opening balance ({currency})', { currency: filters.currency })}</>} onClose={() => setShowOpeningBalanceModal(false)} size="md">

            <form onSubmit={handleSaveOpeningBalance} className="fin-dialog">
              <div className="fin-info">
                <p>{t('Cash on hand = opening balance + approved revenues − approved expenses, for all dates.')}</p>
                <p>
                  <strong>{t('Approved revenues:')}</strong> {formatCurrency(cashOnHand?.approved_revenues ?? 0, filters.currency)}
                  {' · '}
                  <strong>{t('Approved expenses:')}</strong> {formatCurrency(cashOnHand?.approved_expenses ?? 0, filters.currency)}
                </p>
              </div>

              <div className="fin-field">
                <label className="field-label">{t('Opening balance *')}</label>
                <input className="input"
                  type="number"
                  step="0.01"
                  value={openingBalanceInput}
                  onChange={(e) => setOpeningBalanceInput(e.target.value)}
                  required
                />
              </div>

              <div className="fin-form-actions">
                <button type="button" onClick={() => setShowOpeningBalanceModal(false)} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  {t('Save')}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {showEditRevenueModal && editingRevenue && (
        <Modal open title={t('Edit Revenue')} onClose={() => setShowEditRevenueModal(false)} size="md">

            <form onSubmit={handleUpdateRevenue} className="fin-dialog">
              <div className="fin-info">
                <p><strong>{t('Category:')}</strong> {t(editingRevenue.category)}</p>
              </div>

              <div className="fin-form-row">
                <div className="fin-field">
                  <label className="field-label">{t('Amount *')}</label>
                  <input className="input"
                    type="number"
                    value={editingRevenue.amount}
                    onChange={(e) => setEditingRevenue({...editingRevenue, amount: e.target.value})}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="fin-field">
                  <label className="field-label">{t('Currency *')}</label>
                  <select className="select"
                    value={editingRevenue.currency}
                    onChange={(e) => setEditingRevenue({...editingRevenue, currency: e.target.value})}
                    required
                  >
                    <option value="USD">USD</option>
                    <option value="SRD">SRD</option>
                  </select>
                </div>
              </div>

              <div className="fin-form-row">
                <div className="fin-field">
                  <label className="field-label">{t('Date *')}</label>
                  <input className="input"
                    type="date"
                    value={editingRevenue.revenue_date}
                    onChange={(e) => setEditingRevenue({...editingRevenue, revenue_date: e.target.value})}
                    required
                  />
                </div>

                <div className="fin-field">
                  <label className="field-label">{t('Payment Method')}</label>
                  <select className="select"
                    value={editingRevenue.payment_method || ''}
                    onChange={(e) => setEditingRevenue({...editingRevenue, payment_method: e.target.value})}
                  >
                    <option value="">{t('Select method...')}</option>
                    <option value="Bank Transfer">{t('Bank Transfer')}</option>
                    <option value="Cash">{t('Cash')}</option>
                    <option value="Credit Card">{t('Credit Card')}</option>
                    <option value="Check">{t('Check')}</option>
                    <option value="Other">{t('Other')}</option>
                  </select>
                </div>
              </div>

              <div className="fin-field">
                <label className="field-label">{t('Description')}</label>
                <textarea className="textarea"
                  value={editingRevenue.description || ''}
                  onChange={(e) => setEditingRevenue({...editingRevenue, description: e.target.value})}
                />
              </div>

              <div className="fin-form-actions">
                <button type="button" onClick={() => setShowEditRevenueModal(false)} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  {t('Update Revenue')}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* EDIT BUDGET MODAL */}
      {showEditBudgetModal && editingBudget && (
        <Modal open title={t('Edit Budget')} onClose={() => setShowEditBudgetModal(false)} size="md">

            <form onSubmit={handleUpdateBudget} className="fin-dialog">
              <div className="fin-info">
                <p><strong>{t('Category:')}</strong> {t(editingBudget.category)}</p>
                <p><strong>{t('Currency:')}</strong> {editingBudget.currency}</p>
              </div>

              <div className="fin-form-row">
                <div className="fin-field">
                  <label className="field-label">{t('Amount *')}</label>
                  <input className="input"
                    type="number"
                    value={editingBudget.amount}
                    onChange={(e) => setEditingBudget({...editingBudget, amount: e.target.value})}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="fin-field">
                  <label className="field-label">{t('Period Type *')}</label>
                  <select className="select"
                    value={editingBudget.period_type}
                    onChange={(e) => setEditingBudget({...editingBudget, period_type: e.target.value})}
                    required
                  >
                    <option value="monthly">{t('Monthly')}</option>
                    <option value="quarterly">{t('Quarterly')}</option>
                    <option value="yearly">{t('Yearly')}</option>
                  </select>
                </div>
              </div>

              <div className="fin-form-row">
                <div className="fin-field">
                  <label className="field-label">{t('Start Date *')}</label>
                  <input className="input"
                    type="date"
                    value={editingBudget.start_date}
                    onChange={(e) => setEditingBudget({...editingBudget, start_date: e.target.value})}
                    required
                  />
                </div>

                <div className="fin-field">
                  <label className="field-label">{t('End Date')}</label>
                  <input className="input"
                    type="date"
                    value={editingBudget.end_date || ''}
                    onChange={(e) => setEditingBudget({...editingBudget, end_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="fin-field">
                <label className="fin-check">
                  <input
                    type="checkbox"
                    checked={editingBudget.is_active}
                    onChange={(e) => setEditingBudget({...editingBudget, is_active: e.target.checked})}
                  />
                  <span>{t('Active')}</span>
                </label>
              </div>

              <div className="fin-form-actions">
                <button type="button" onClick={() => setShowEditBudgetModal(false)} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  {t('Update Budget')}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* UPLOAD RECEIPT MODAL */}
      {showReceiptModal && selectedExpense && (
        <Modal open title={t('Upload Receipt')} onClose={() => setShowReceiptModal(false)} size="md">

            <div className="fin-dialog">
              <div className="fin-info">
                <p><strong>{t('Expense:')}</strong> {t(selectedExpense.category)}</p>
                <p><strong>{t('Amount:')}</strong> {formatCurrency(selectedExpense.amount, selectedExpense.currency)}</p>
              </div>

              <div className="fin-field">
                <label className="field-label">{t('Select Receipt File')}</label>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  onChange={handleFileChange}
                />
                <small className="field-hint">{t('Allowed: JPG, PNG, PDF (Max 5MB)')}</small>
                {selectedFile && (
                  <div className="fin-file">
                    <Paperclip aria-hidden="true" /> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </div>
                )}
              </div>

              <div className="fin-form-actions">
                <button onClick={() => {
                  setShowReceiptModal(false);
                  setSelectedExpense(null);
                  setSelectedFile(null);
                }} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button onClick={handleUploadReceipt} className="btn btn--primary">
                  {t('Upload Receipt')}
                </button>
              </div>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default Finance;